import { isEdge, Node } from 'react-flow-renderer';

import { NodeTypes } from './NodeTypes';

import ELKConstructor, { ElkExtendedEdge, ElkNode } from 'elkjs';
import { useCallback } from 'react';

const elk = new ELKConstructor({
  defaultLayoutOptions: {
    // v1
    'elk.algorithm': 'layered',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.alignment': 'CENTER',
    // To encourage ELK to layout in a symmetric way set this to false
    'elk.layered.nodePlacement.favorStraightEdges': 'false',
    'elk.layered.crossingMinimization.semiInteractive': 'true',
    // Maintain the order that we specified in.
    'elk.layered.considerModelOrder': 'NODES_AND_EDGES',
    // Layout downwards.
    'elk.direction': 'DOWN',
    // Spacing of nodes and layers.
    'elk.spacing.nodeNode': '190',
    'elk.spacing.edgeNode': '100',
    'elk.spacing.edgeEdge': '0',
    'elk.layered.spacing.nodeNodeBetweenLayers': '70',
    'elk.layered.spacing.edgeNodeBetweenLayers': '0',
    'elk.layered.spacing.edgeEdgeBetweenLayers': '0',
  },
});

const DATE_ERROR_HEIGHT = 40;

export const getNodeSize = (type?: NodeTypes, node?: Node<any>) => {
  switch (type) {
    case NodeTypes.SCHEDULER: {
      const rootOnly = !node?.data?.schedulerDateTimeVisible;
      const height = rootOnly ? 70 : 175 + DATE_ERROR_HEIGHT;
      return [448, height];
    }
    case NodeTypes.DELAY:
      return [448, 70 + DATE_ERROR_HEIGHT];
    case NodeTypes.DIALOGUE:
      return [448, 215 + DATE_ERROR_HEIGHT];
    case NodeTypes.CONDITION:
      return [630, 230];
    case NodeTypes.UNICORN_DETECTOR:
      return [490, 254];
    case NodeTypes.ADDER:
      return [130, 28];
    case NodeTypes.ZAPIER:
    case NodeTypes.JSON_WEBHOOK:
    case NodeTypes.MAILCHIMP:
    case NodeTypes.GOOGLE_SHEET:
    case NodeTypes.FACEBOOK_ADS:
      return [448, 80];
    default:
      return [0, 0];
  }
};

// temporary disabled as using predefined sizes per type
// export const useNodeSize = () => {
//   const nodes = useStoreState(state => state.nodes);
//
//   return useCallback(
//     (id: string) => {
//       const match = nodes.find(n => n.id.toString() === id);
//       const { width, height } = match?.__rf || {};
//       return { width, height };
//     },
//     [nodes],
//   );
// };

export const useLayout = () => {
  return useCallback(async elements => {
    const graph = elements.reduce(
      (graph, node) => {
        if (isEdge(node)) {
          graph.edges.push({
            ...node,
            sources: [node?.source],
            targets: [node?.target],
          });
        } else {
          const [w, h] = getNodeSize(node.type, node);

          graph.children.push({
            ...node,
            width: w,
            height: h,
          });
        }
        return graph;
      },
      {
        id: 'root',
        edges: [],
        children: [],
      },
    );

    const res = await elk.layout(graph);

    return [...(res?.children || []), ...(res.edges || [])]
      .filter(Boolean)
      .map(v => {
        if ((v as ElkExtendedEdge)?.sections?.length) {
          // change to external validation by type
          const { id, targets, sources, ...rest } = v as ElkExtendedEdge;

          return { id, target: targets[0], source: sources[0], ...rest };
        }

        const { x, y, ...rest } = v as ElkNode;
        return { ...rest, position: { x, y } };
      });
  }, []);
};
